import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Save } from "lucide-react";
import Avatar from "@/components/Avatar";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    character: user?.character || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      const upd = { name: form.name, email: form.email, phone: form.phone, character: form.character };
      if (form.password) upd.password = form.password;
      await api.patch(`/users/${user.id}`, upd);
      toast.success("Profile updated");
      await refresh();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/users/${user.id}/avatar`, fd);
      toast.success("Headshot updated");
      setAvatarKey(Date.now());
      await refresh();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Your profile</DialogTitle>
          <DialogDescription className="text-zinc-500">Upload a headshot and update your contact info.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar user={user} size="w-20 h-20" textClass="text-2xl" refresh={avatarKey} />
            <button
              type="button"
              data-testid="profile-avatar-upload-btn"
              onClick={() => fileRef.current?.click()}
              className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full grid place-items-center bg-gold text-black border-2 border-[#121212] hover:brightness-110"
              title="Upload headshot"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="profile-avatar-input" onChange={uploadAvatar} />
          </div>
          <div>
            <div className="font-display text-lg">{user.name}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest">{user.role}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Full name</Label><Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Email</Label><Input data-testid="profile-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div className="col-span-2"><Label>Character / Role note</Label><Input value={form.character} onChange={(e) => setForm({ ...form, character: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div className="col-span-2"><Label>New password <span className="text-zinc-600 text-xs">(leave empty to keep current)</span></Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
        </div>
        <DialogFooter>
          <button data-testid="profile-save-btn" onClick={save} disabled={saving} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5">
            <Save className="w-4 h-4" /> Save profile
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
